'use client';

import * as React from 'react';

import { cn } from '~/lib/utils';
import { Input } from '~/components/ui/input';
import { useRef } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { type Url } from 'next/dist/shared/lib/router/router';
import { Loader2, SearchIcon, XIcon } from 'lucide-react';
import { type Website, type components } from '~/fecom-api/schema';
import { FecomImage } from '~/components/images/fecom-image';
import { Price } from '~/components/products/price';
import { Button } from '~/components/ui/button';
import { tailwindBreakpointSizesToImageSizes } from '~/utils/images/tailwind-breakpoints-to-image-sizes';
import { trpc } from '~/trpc/client';
import { useDebounceValue } from 'usehooks-ts';

type SearchInputProps = {
  //
  websiteId: Website;
};

type IdTracker = {
  id: string;
  type: 'suggestion' | 'product';
};

export function SearchBox({ websiteId }: SearchInputProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const ref = useRef<HTMLInputElement>(null);

  const searchItemId = React.useId();

  const phrase = searchParams.get('phrase') ?? '';
  const [value, setValue] = React.useState(phrase ?? '');
  const [debouncedValue, updateDebouncedValue] = useDebounceValue(phrase, 200);

  React.useEffect(() => {
    updateDebouncedValue(value);
  }, [updateDebouncedValue, value]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectionId !== null) {
      return;
    }
    const formData = new FormData(e.currentTarget);
    const phrase = formData.get('phrase') as string;
    const trimmedPhrase = phrase.trim();
    if (trimmedPhrase) {
      void router.push(
        `/search?phrase=${encodeURIComponent(trimmedPhrase).replace(/%20/g, '+')}`,
      );
      ref.current?.blur();
    }
  };

  React.useEffect(() => {
    if (pathname !== '/search') {
      setValue('');
    }

    if (phrase) {
      setValue(phrase ?? '');
    }
  }, [pathname, phrase]);

  // const search = useSearchQuery({ phrase: value });
  const search = trpc.search.searchAutoComplete.useQuery(
    {
      phrase: debouncedValue,
      limit: 5,
    },
    {
      enabled: debouncedValue.length >= 2,
      staleTime: 1000 * 60 * 3, // 3 mins
    },
  );

  const suggestions = search.data?.searchSuggestions ?? [];
  const products = search.data?.products ?? [];

  const suggestionsIds = suggestions.map((suggestion) => {
    const tracker: IdTracker = {
      id: suggestion.suggestion,
      type: 'suggestion',
    };
    return tracker;
  });

  const productsIds = products.map((product) => {
    const tracker: IdTracker = { id: product.itemCode, type: 'suggestion' };
    return tracker;
  });

  const ids: IdTracker[] = suggestionsIds.concat(productsIds);

  const [selectionId, setSelectionId] = React.useState<string | null>(null);

  const router = useRouter();

  const showResults = value.length >= 2;

  React.useEffect(() => {
    if (selectionId) {
      document
        .getElementById(`${searchItemId}-${selectionId}`)
        ?.scrollIntoView({
          block: 'nearest',
        });
    }
  }, [searchItemId, selectionId]);

  const hasNoResults = suggestions.length === 0 && products.length === 0;

  return (
    <form
      className="search-box relative w-full text-foreground"
      onSubmit={handleSubmit}
    >
      <div className="group relative">
        <Input
          data-hj-allow
          ref={ref}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setSelectionId(null);
          }}
          name="phrase"
          type="search"
          placeholder="Search for a product"
          tabIndex={0}
          className={cn(
            'search-box__input',
            'relative z-30 py-0 pr-16 text-base',
            // remove default search input styles
            '[appearance:textfield] [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none',
          )}
          onKeyUp={(e) => {
            if (e.key === 'Enter') {
              if (!selectionId) {
                return;
              }

              e.preventDefault();
              e.stopPropagation();
              const suggestion = suggestions.find(
                (s) => s.suggestion === selectionId,
              );

              if (suggestion) {
                void fetch(suggestion.logUrl).catch((error) => {
                  console.log('Error logging SLI click:', error);
                });
                router.push(
                  `/search?phrase=${encodeURIComponent(selectionId).replace(/%20/g, '+')}`,
                );
              }

              const product = products.find((p) => p.itemCode === selectionId);

              if (product) {
                product.sliLogUrl &&
                  void fetch(product.sliLogUrl).catch((error) => {
                    console.log('Error logging SLI click:', error);
                  });

                router.push(product.urlPath);
              }

              e.currentTarget.blur();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();

              const currentIndex = ids.findIndex((id) => id.id === selectionId);
              const nextIndex = Math.min(currentIndex + 1, ids.length - 1);
              setSelectionId(ids?.at(nextIndex)?.id ?? null);
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();

              const currentIndex = ids.findIndex((id) => id.id === selectionId);
              const nextIndex = Math.max(currentIndex - 1, -1);
              if (nextIndex === -1) {
                setSelectionId(null);
              } else {
                setSelectionId(ids?.at(nextIndex)?.id ?? null);
              }
            }
          }}
        />
        {!!value && (
          <Button
            variant="custom"
            size="icon"
            className="search-box__clear absolute right-8 top-0 z-30 text-muted-foreground focus-visible:ring-inset"
            onClick={() => {
              setValue('');
              setSelectionId(null);
              ref.current?.focus();
            }}
            type="button"
          >
            <span className="sr-only">Clear search</span>
            <XIcon className="search-box__clear-icon size-5" />
          </Button>
        )}
        <Button
          type="submit"
          variant="custom"
          size="icon"
          className="search-box__search absolute right-0 top-0 z-30 text-muted-foreground focus-visible:ring-inset"
        >
          <span className="sr-only">Search</span>
          <SearchIcon className="search-box__search-icon h-5 w-5" />
        </Button>
        {!!value && (
          <div className="pointer-events-none fixed inset-0 z-10 hidden bg-black/20 group-focus-within:block" />
        )}
        {showResults && (
          <div
            role="menu"
            className={cn(
              'search-box__results',
              'absolute left-1/2 -translate-x-1/2',
              'w-full md:w-[30rem] xl:w-[40rem] 2xl:w-[48rem]',
              'top-full z-50 hidden max-h-[80svh] min-h-24 scroll-p-16 flex-col gap-5 overflow-auto bg-white px-2 pb-2 shadow-lg group-focus-within:flex',
            )}
          >
            <div
              className={cn(
                'search-box__loading',
                'pointer-events-none absolute inset-0 z-50 flex place-items-center justify-center bg-white/50 py-2 opacity-0 transition-opacity',
                {
                  'opacity-100': search.isLoading || search.isPlaceholderData,
                },
              )}
            >
              <p>
                <span className="sr-only">Loading...</span>
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </p>
            </div>
            {!search.isLoading && !search.isPlaceholderData && hasNoResults && (
              <div
                role="menuitem"
                className="search-box__no-results flex grow flex-col items-center justify-center"
              >
                <p className="text-center text-muted-foreground">
                  No results found
                </p>
              </div>
            )}
            {suggestions.length > 0 && (
              <div role="group">
                <SearchHeading>Search suggestions</SearchHeading>
                <div>
                  {suggestions.map((suggestion) => {
                    return (
                      <div key={suggestion.suggestion} role="menuitem">
                        <SearchItem
                          id={searchItemId + '-' + suggestion.suggestion}
                          href={`/search?phrase=${encodeURIComponent(suggestion.suggestion).replace(/%20/g, '+')}`}
                          selected={selectionId === suggestion.suggestion}
                          sliLogUrl={suggestion.logUrl}
                        >
                          <SearchSuggestion
                            searchPhrase={value}
                            suggestion={suggestion.suggestion}
                            className="px-2"
                          />
                        </SearchItem>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {products.length > 0 && search.data ? (
              <div role="group">
                <SearchHeading>Product suggestions</SearchHeading>
                <div className="grid grid-cols-2 gap-4 xl:grid-cols-3 2xl:grid-cols-4">
                  {products.map((product, index) => (
                    <div
                      key={product.productId}
                      role="menuitem"
                      className={cn({
                        'hidden xl:flex': index === 2,
                        'hidden 2xl:flex': index === 3,
                      })}
                    >
                      <SearchItem
                        id={searchItemId + '-' + product.itemCode}
                        href={{
                          pathname: product.urlPath,
                          query: { phrase: value },
                        }}
                        as={product.urlPath}
                        selected={selectionId === product.itemCode}
                        sliLogUrl={product.sliLogUrl}
                      >
                        <SearchProduct
                          websiteId={websiteId}
                          product={product}
                          fecomMediaBaseUrl={search.data?.fecomMediaUrl}
                        />
                      </SearchItem>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </form>
  );
}

type SearchHeadingProps = {
  children: React.ReactNode;
};

function SearchHeading({ children }: SearchHeadingProps) {
  return (
    <h3 className="mb-2 border-b border-border p-2 font-bold tracking-wide">
      {children}
    </h3>
  );
}

type SearchItemProps = {
  id: string;
  href: Url;
  as?: Url;
  selected: boolean;
  children: React.ReactNode;
  className?: string;
  sliLogUrl?: string | null;
};

function SearchItem({
  id,
  href,
  as,
  selected,
  children,
  className,
  sliLogUrl,
}: Readonly<SearchItemProps>) {
  const handleTracking = () => {
    if (sliLogUrl) {
      void fetch(sliLogUrl).catch((error) => {
        console.log('Error logging SLI click:', error);
      });
    }
  };

  return (
    <Link
      id={id}
      tabIndex={-1}
      className={cn('flex h-full flex-col rounded-lg p-2 hover:bg-gray-100', {
        'relative z-10 ring ring-ring': selected,
        className,
      })}
      href={href}
      as={as}
      onClick={(e) => {
        handleTracking();
        e.currentTarget.blur();
      }}
      onAuxClick={() => {
        handleTracking();
      }}
    >
      {children}
    </Link>
  );
}

type SearchSuggestionProps = {
  className?: string;
  searchPhrase: string;
  suggestion: string;
};

function SearchSuggestion({
  className,
  searchPhrase,
  suggestion,
}: SearchSuggestionProps) {
  return (
    <p className={cn('font-bold', className)}>
      {highlightSearchPhrase(searchPhrase, suggestion)}
    </p>
  );
}

type SearchProductProps = {
  product: components['schemas']['Product'];
  fecomMediaBaseUrl?: string | null;
  className?: string;
  websiteId: Website;
};

function SearchProduct({
  product,
  fecomMediaBaseUrl,
  className,
  websiteId,
}: SearchProductProps) {
  return (
    <article className={cn('flex h-full flex-col gap-2', className)}>
      <div className="relative aspect-square w-full">
        {!!fecomMediaBaseUrl && (
          <FecomImage
            fecomMediaBaseUrl={fecomMediaBaseUrl}
            src={product.imageId}
            alt={'Image of ' + product.productName}
            fill
            sizes={tailwindBreakpointSizesToImageSizes(
              { md: 208, xl: 181, '2xl': 160 },
              '50vw',
            )}
            className="absolute inset-0 rounded-lg bg-white object-contain"
          />
        )}
      </div>
      <div className="flex grow flex-col gap-2">
        <h4 className="font-semibold leading-5">{product.productName}</h4>
        <div className="mt-auto">
          {!!product.wasPriceIncVat && !!product.wasPriceExcVat && (
            <p className="text-xs text-[#626262] line-through">
              <Price
                priceIncVat={product.wasPriceIncVat}
                priceExcVat={product.wasPriceExcVat}
                currency={product.currency}
                showVatStatusText
                className="block"
                vatStatusClassName="text-xs"
                websiteId={websiteId}
              />
            </p>
          )}
          <p className="text-lg font-semibold">
            <Price
              websiteId={websiteId}
              priceIncVat={product.priceIncVat}
              priceExcVat={product.priceExcVat}
              showVatStatusText
              currency={product.currency}
              className={cn('block', {
                'text-success':
                  !!product.wasPriceIncVat && !!product.wasPriceExcVat,
              })}
              vatStatusClassName="text-[#626262]"
            />
          </p>
        </div>
      </div>
    </article>
  );
}

function highlightSearchPhrase(
  searchPhrase: string,
  searchSuggestion: string,
): React.JSX.Element {
  const words: string[] = searchPhrase.trim().toLowerCase().split(' ');
  const regex = new RegExp(`(${words.join('|')})`, 'gi');
  const parts: string[] = searchSuggestion.split(regex).filter(Boolean);

  return (
    <span>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <span className="font-normal" key={index}>
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </span>
  );
}
